<template>
  <v-card>
    <v-card-title primary-title>
      <div>
        <div class="headline secondary--text">
          {{ $t("screener.monetization.group_ticket.title") }}
        </div>
        <span class="body-1 greyDarken1--text">{{
          $t("screener.monetization.group_ticket.subtitle")
        }}</span>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        color="secondary"
        dark
        class="mx-0"
        @click="$router.push({ name: 'group.ticket.create' })"
      >
        <v-icon small>$vuetify.icons.rms_create</v-icon>
        {{ $t("rms.button.create") }}
      </v-btn>
    </v-card-title>
    <v-layout row wrap class="pa-3">
      <v-flex xs12 sm3 md3>
        <v-text-field
          append-outer-icon="search"
          v-model="search"
          :placeholder="$t('rms.field.search')"
          clear-inner-icon="rms_close"
          clearable
          single-line
          hide-details
          @click:clear="clearSearch"
          @click:append-outer="applySearch"
        ></v-text-field>
      </v-flex>
      <v-spacer></v-spacer>
    </v-layout>

    <v-data-table
      :headers="table_headers"
      :items="tickets"
      :loading="loading"
      :pagination.sync="pagination"
      :total-items="total"
      :rowsPerPageItems="rowsPerPageItems"
      item-key="id"
    >
      <template slot="headerCell" slot-scope="props">
        <v-tooltip top v-if="props.header.tooltip.length" color="greyDarken1">
          <template v-slot:activator="{ on }">
            <span v-on="on">{{ props.header.text }}</span>
          </template>
          <span class="white--text body-1">{{ props.header.tooltip }}</span>
        </v-tooltip>
        <span v-else>{{ props.header.text }}</span>
      </template>
      <template v-slot:items="props">
        <tr @click.stop="viewTicket(props.item.id)" style="cursor: pointer">
          <td class="body-2">
            <v-tooltip top color="greyDarken1">
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ props.item.name | truncate }}</span>
              </template>
              <span class="white--text body-1">{{ props.item.name }}</span>
            </v-tooltip>
          </td>
          <td>
            {{ props.item.screening_ids.length }}
            <!-- <template v-for="(screening, index) in props.item.screenings">
              <v-tooltip
                v-if="index < 3"
                top
                color="greyDarken1"
                :key="screening.id"
              >
                <template v-slot:activator="{ on }">
                  <v-chip
                    v-on="on"
                    color="secondary"
                    outline
                    small
                    :key="screening.id"
                    >{{ screening.title | truncate }}</v-chip
                  >
                </template>
                <span class="white--text body-1">{{ screening.title }}</span>
              </v-tooltip></template
            >

            <v-chip
              v-if="props.item.screenings.length > 3"
              color="primary"
              text-color="white"
              small
            >
              +{{ props.item.screenings.length - 3 }}&nbsp;{{
                $t("screener.more")
              }}
            </v-chip> -->
          </td>
          <td>
            {{ props.item.purchased_count }}&nbsp;/&nbsp;{{
              props.item.number_of_sales
            }}
          </td>
          <td>
            {{ fmtTime(props.item.purchasing_start_date) }}
            <b>(to)</b>
            {{ fmtTime(props.item.purchasing_end_date) }}
          </td>
          <td>{{ props.item.price }}</td>
          <td>{{ props.item.currency }}</td>
          <td>
            <v-chip
              class="secondary secondary--text font-weight-medium"
              small
              outline
              >{{ DEFINES.SCREENER_STATUS[props.item.published] }}</v-chip
            >
          </td>
          <td>{{ fmtTime(props.item.created_at) }}</td>
          <td>
            <v-tooltip slot="append" top color="white">
              <v-btn
                slot="activator"
                color="primary"
                flat
                icon
                small
                @click.stop="setPreview(props.item)"
              >
                <v-icon>mdi-file-eye-outline</v-icon>
              </v-btn>
              <span class="greyDarken3--text body-1">
                {{ $t("screener.btn.preview") }}
              </span>
            </v-tooltip>
            <v-tooltip slot="append" top color="white">
              <v-btn
                slot="activator"
                color="primary"
                flat
                icon
                small
                @click.stop="viewTicket(props.item.id)"
              >
                <v-icon>$vuetify.icons.rms_visibility</v-icon>
              </v-btn>
              <span class="greyDarken3--text body-1">
                {{ $t("rms.tooltip.view") }}
              </span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>
    <GroupTicketPreview
      :passPreview.sync="preview"
      :ticket="ticket"
    ></GroupTicketPreview>
  </v-card>
</template>

<script>
import DEFINES from "@/defines";
import rules from "@/validation";
import i18n from "@/i18n";
import { mapState, mapGetters } from "vuex";
import moment from "moment";
import { utcToTz } from "@/helper";
import { getPricingListFromApi } from "@/mixins/monetization";
import GroupTicketPreview from "@/components/screener/monetization/GroupTicketPreview";

// data
const data = {
  table_headers: [
    {
      text: "Ticket Name",
      sortable: true,
      align: "left",
      value: "name",
      tooltip: "",
    },
    {
      text: "Total Screenings",
      sortable: false,
      align: "left",
      value: "screening_ids",
      tooltip: "",
    },
    {
      text: "Number of sales",
      sortable: true,
      align: "left",
      value: "number_of_sales",
      tooltip: "The ratio of purchased count & total available sales",
    },
    {
      text: "Ticket Sale Starts At / Ends At",
      sortable: false,
      align: "left",
      value: "purchasing_end_date",
      tooltip: "",
    },
    {
      text: "Price",
      sortable: true,
      align: "left",
      value: "price",
      tooltip: "",
    },
    {
      text: "Currency",
      align: "left",
      sortable: true,
      value: "currency",
      tooltip: "",
    },
    {
      text: "Status",
      align: "left",
      value: "published",
      sortable: true,
      tooltip: "",
    },
    {
      text: "Created At",
      align: "left",
      value: "created_at",
      sortable: true,
      tooltip: "",
    },
    {
      text: "Action",
      align: "left",
      value: "action",
      sortable: true,
      tooltip: "",
    },
  ],
  pagination: {
    sortBy: "created_at",
    descending: true,
    page: 1,
    rowsPerPage: 10,
  },
  rowsPerPageItems: [5, 10, 25, 50, 100],
  search: "",
  loading: false,
  total: null,
  preview: false,
  ticket: null,
};

const components = {
  GroupTicketPreview,
};
// Computed
const computed = {
  ...mapState({
    tickets: (state) => state.monetization.list,
  }),
  ...mapGetters("company", {
    timezone: "getSelectedCompanyTimezone",
  }),
};

const mixins = [getPricingListFromApi];

// Methods
const methods = {
  applySearch() {
    if (this.search.length) this.getDataFromApi();
  },
  clearSearch() {
    this.search = "";
    this.getDataFromApi();
  },
  // convert utc to comapny timezone
  fmtTime(input) {
    return utcToTz(input, this.timezone, "Do MMM YYYY, h:mm a");
  },
  viewTicket(pricing_id) {
    this.$router.push({
      name: "group.ticket.save",
      params: {
        pricing_id: pricing_id,
      },
    });
  },
  setPreview(ticket) {
    this.preview = true;
    this.ticket = ticket;
  },
  async getDataFromApi() {
    try {
      var response = await this.getPricingListFromApi(
        this.getPaginationPayload()
      );

      if (response) {
        this.total = response.pricing.total;
      }
      this.loading = false;
    } catch (error) {
      this.loading = false;
      this.$store.dispatch("app/showMessage", {
        message: error.message,
        color: this.DEFINES.SNACKBAR_ERROR_COLOR,
      });
    }
  },
  getPaginationPayload() {
    this.loading = true;
    // Pagination
    const { sortBy, descending, page, rowsPerPage } = this.pagination;
    var offset = this.getPaginationOffset(rowsPerPage, page);
    // Payload
    var payload = {
      offset: offset,
      limit: rowsPerPage,
      sort: sortBy,
      sorting_order: descending,
      search: this.search,
      filters: {},
    };
    return payload;
  },
  getPaginationOffset: (recordPerPage, page) => recordPerPage * (page - 1),
};

export default {
  name: "GroupTicketList",
  data() {
    return data;
  },
  computed: computed,
  mixins: mixins,
  components: components,
  watch: {
    pagination: {
      handler() {
        this.getDataFromApi();
      },
      deep: true,
    },
  },
  methods: methods,
  mounted() {
    this.getDataFromApi();
  },
};
</script>


